/**
 * 손그림 UI 아이콘 — **운영자 그림 → 화면 묶음 → 낱장 → 이모지** 4단 폴백.
 *
 * 목록·이모지·기본 경로는 shared `UI_ART` 하나에서 온다(관리자 목록과 같은 정의).
 *
 * 🧩 **화면 묶음**(2026-09-27 · 조치계획서 ■2): 화면이 `<UiAtlas bundle="book">` 로 감싸여 있고
 * 그 묶음에 이 아이콘이 있으면, 낱장 대신 **묶음 한 장**에서 제 칸만 잘라 보여 준다 — 단추가 아홉이어도
 * 요청은 하나다. 운영자가 관리자에서 올린 그림은 묶음에 없으니 **그것만 낱장으로** 받는다(운영자가 이긴다).
 *
 * 장식 요소이므로 alt=""·aria-hidden — 의미는 버튼의 aria-label 이나 옆 글자가 전달한다.
 */
import { createContext, useContext, useState, type ReactNode } from "react";
import {
  UI_ATLAS,
  uiArtSlot,
  uiAtlasIndex,
  uiAtlasUrl,
  type UiArtGroup,
  type UiAtlasBundle,
} from "@greg-chick/shared";
import { useUiArtEx } from "../lib/uiArt";
import { artUrl } from "../lib/artVersion";

export type UiIconName =
  | "mission"
  | "camera"
  | "sound_on"
  | "sound_off"
  | "map"
  | "pin"
  | "sparkle"
  | "megaphone"
  | "prev"
  | "next"
  | "pause"
  | "subtitle"
  | "close"
  | "back"
  | "play"
  | "door"
  | "maker"
  | "star"
  | "mybook"
  | "gallery"
  | "key"
  | "print"
  | "home"
  | "cube"
  | "village"
  | "gyeongju"
  | "stamp"
  | "guide"
  | "teddy"
  | "grid"
  | "leaf"
  | "reset"
  | "privacy"
  | "tools"
  | "palette"
  | "dice"
  | "check"
  | "passport"
  | "document"
  | "load"
  | "skip"
  | "globe";

const AtlasContext = createContext<UiAtlasBundle | undefined>(undefined);

/** 이 화면이 쓰는 아이콘 묶음. 라우트 한 곳(App)에서 감싼다. */
export function UiAtlas({ bundle, children }: { bundle: UiAtlasBundle; children: ReactNode }) {
  return <AtlasContext.Provider value={bundle}>{children}</AtlasContext.Provider>;
}

export function UiIcon({
  name,
  group = "act",
  size = 22,
  className,
}: {
  name: UiIconName | string;
  /** 기본 `act`. 아래 메뉴 그림을 단추에 다시 쓸 때는 `nav`(예: AR 로 보기 = 마법 카메라). */
  group?: UiArtGroup;
  size?: number;
  className?: string;
}) {
  const bundle = useContext(AtlasContext);
  const art = useUiArtEx();
  const [failed, setFailed] = useState<"atlas" | "single" | null>(null);
  const got = art(group, name);
  const emoji = uiArtSlot(group, name)?.emoji ?? "•";
  const index = uiAtlasIndex(bundle, group, name);

  // ② 화면 묶음 — 운영자가 올린 그림이 없고, 묶음에 칸이 있고, 묶음이 안 깨졌을 때
  if (got.from !== "published" && bundle && index >= 0 && failed !== "atlas" && failed !== "single") {
    const count = UI_ATLAS[bundle].length;
    return (
      <span
        className={className ? `ui-sprite ${className}` : "ui-sprite"}
        aria-hidden="true"
        data-atlas={bundle}
        style={{ width: size, height: size }}
      >
        <img
          src={artUrl(uiAtlasUrl(bundle))}
          alt=""
          draggable={false}
          width={size * count}
          height={size}
          style={{ width: size * count, height: size, transform: `translateX(${-index * size}px)` }}
          // 묶음이 안 오면 낱장으로 — 그것도 안 오면 이모지로(뜻은 남는다)
          onError={() => setFailed("atlas")}
        />
      </span>
    );
  }
  // ①·③ 운영자 그림 또는 낱장
  if (got.url && failed !== "single") {
    return (
      <img
        className={className}
        src={got.url}
        alt=""
        aria-hidden="true"
        draggable={false}
        width={size}
        height={size}
        style={{ width: size, height: size, objectFit: "contain" }}
        onError={() => setFailed("single")}
      />
    );
  }
  // ④ 이모지
  return (
    <span className={className} aria-hidden="true" style={{ fontSize: size * 0.86 }}>
      {emoji}
    </span>
  );
}
